<template>
  <div class="answers_value answers_file">
    <label class="file-title" for="resumeFile">{{
      $tr('vacancy.preferences.application.labelResume')
    }}</label>
    <div class="file-wrapper">
      <input
        id="resumeLink"
        readonly
        type="text"
        :disabled="block"
        :value="resumeFile ? resumeFile.name : ''"
        :placeholder="upload_file"
        :class="{
          error: errors.has('resumeFile'),
          valid: fields.resumeFile ? fields.resumeFile.valid : null
        }"
      />
      <input
        :id="`resumeFile_${id}`"
        ref="fileSelector"
        v-validate="{
          required: true,
          size: 15000
        }"
        name="resumeFile"
        type="file"
        accept="image/*, .xlsx, .xls, application/pdf, .doc, .docx, .csv, .pot, .potx"
        multiple="multiple"
        :class="{
          error: errors.has('resumeFile'),
          valid: fields.resumeFile ? fields.resumeFile.valid : null
        }"
        @change="upload($event.target.files)"
      />
      <div class="actions">
        <label v-if="!resumeFile" :for="`resumeFile_${id}`">
          <icon-upload :width="'31px'" :height="'20px'" />
        </label>
        <div v-else class="pointer" @click="reset">
          <icon-close />
        </div>
      </div>
      <span v-if="isMobile && errors.has('resumeFile')" class="error-mobile">
        {{ $tr('responses.sendMessage.warnings.wrongFormat') }}
      </span>
      <div v-if="err_message" class="error">{{ err_message }}</div>
    </div>
  </div>
</template>

<script>
  import axios from 'axios';
  import isMobile from 'ismobilejs';
  import { parseResponse } from '@/_helpers/utils.js';
  import IEBrowserDetector from '@/components/mixin/IEBrowserDetector.js';

  const MAX_SIZE_BYTES = 15 * 1024 * 1024;

  export default {
    name: 'AnswersValue',
    mixins: [IEBrowserDetector],
    props: ['data', 'isDisabled', 'token', 'index'],
    data() {
      return {
        answer: '',
        resumeFile: null,
        err_message: '',
        isMobile: isMobile.any,
        url: ''
      };
    },

    computed: {
      ...mapState('tests_interview', {
        interview: 'item'
      }),

      file_name() {
        let name = '';
        if (this.interview && this.interview.candidate) {
          name = `${this.interview.candidate.surname}_${this.interview.candidate.name}_${this.interview.response_id}_T_${this.index}`;
        }
        return name;
      },

      upload_file() {
        if (this.isMobile) {
          return this.$tr('vacancy.preferences.application.labelResume');
        } else {
          return this.$tr('tests.uploadFile');
        }
      },

      block() {
        return this.isIE11OrOlder ? true : false;
      },

      id() {
        if (!this.data || !this.data[0]) {
          return 0;
        }
        return this.data[0].id;
      }
    },

    mounted() {
      this.$emit('loading', false);
    },

    methods: {
      set_answer(v) {
        this.answer = v;
        this.$emit('update', [
          {
            id: this.data[0].id,
            body: v
          }
        ]);
      },

      async upload(files) {
        if (!files.length) return;

        this.resumeFile = files[0];

        if (this.resumeFile.size / MAX_SIZE_BYTES >= 1) {
          this.err_message = this.$tr('vacancy.preferences.application.fileError');
          return;
        }

        let reader = new FileReader();

        reader.onloadend = () => {
          let file = reader.result;
          let blob = file.split('base64,');
          this.set_file(blob[1]);
        };

        reader.readAsDataURL(this.resumeFile);

        const fileUrl = this.resumeFile;
        let data = new FormData();
        data.append('file', fileUrl, new Date().toJSON());
        data.append('filename', this.file_name);
        this.$emit('on-loading', true);
        try {
          await axios
            .post(`1.0/interviews/${this.token}/company-file`, data)
            .then(response => parseResponse(response).data[0])
            .then(data => {
              this.url = data.url;
              this.set_file();
              this.$emit('on-loading', false);
            });
        } catch (err) {
          if (err && err.response && err.response.data && err.response.data.meta) {
            this.$noty.error(err.response.data.meta.error.message);
            this.$emit('on-loading', false);
          }
        }
      },

      set_file(file) {
        if (!this.errors.has('resumeFile')) {
          this.$emit('update', [
            {
              id: this.data[0].id,
              body: this.url
            }
          ]);
        }

        if (this.err_message !== '') {
          this.err_message = '';
        }
      },

      reset() {
        this.resumeFile = null;
        this.$refs.fileSelector.value = '';
        this.$emit('on-loading', false);
        this.$emit('update', 'reset');

        if (!/safari/i.test(navigator.userAgent)) {
          this.$refs.fileSelector.type = '';
          this.$refs.fileSelector.type = 'file';
        }
      }
    }
  };
</script>

<style lang="scss" scoped>
  .answers_value {
    ::v-deep .textarea {
      height: 120px;
    }
  }

  .answers_file {
    .file-title {
      font-size: 15px;
      line-height: 24px;
      color: #233346;
      display: block;
    }

    .file-wrapper {
      position: relative;
      min-width: 440px;
      margin-top: 4px;
      font-size: 15px;
      display: inline-block;

      @media (max-width: 460px) {
        min-width: 240px;
      }

      input[type='text']:read-only {
        pointer-events: none;
        width: 100%;
        border: 1px solid #d2d6e0;
        height: 40px;

        &.error {
          border-color: #fc7170;
        }

        &:focus,
        &:active {
          border-color: #d2d6e0;
        }
      }

      .full-width input[type='text'],
      .full-width textarea {
        border-color: #d2d6e0;
      }

      input:focus,
      textarea:focus {
        border-color: #d2d6e0;
      }

      input[type='file'] {
        width: 0.1px;
        height: 0.1px;
        opacity: 0;
        overflow: hidden;
        position: absolute;
        z-index: -1;

        + .actions {
          display: block;
          position: absolute;
          right: 1px;
          top: 3px;
          background: #fff;
          width: 45px;
          height: 36px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;

          label {
            cursor: pointer;
          }
        }
      }

      input.error {
        + label .icon-upload {
          color: $red-color;
        }
      }

      .error-mobile {
        display: inline-block;
        margin-top: 10px;
        color: #fc7170;
      }
    }
  }

  .pointer {
    cursor: pointer;
  }
</style>
